import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CalendarClock } from 'lucide-react';
import { TASK_DATE_SHIFT_CHUNK, type TaskDateShiftPreview } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { applyTaskDateShift, previewTaskDateShift } from '../../api/event-ops';
import { invalidateTasks } from '../../hooks/useEventOps';
import { extractApiErrorMessage } from '../../utils/apiError';
import {
  onTaskDateShiftRequest,
  type TaskDateShiftRequest,
} from '../../utils/taskDateShiftRequests';

/** "Thu, Oct 1" for a YYYY-MM-DD, read as the calendar day it is. */
function formatDay(key: string): string {
  return new Date(`${key}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function describeOffset(days: number): string {
  if (days === 0) return 'on the event day';
  const n = Math.abs(days);
  return `${n} day${n === 1 ? '' : 's'} ${days < 0 ? 'before' : 'after'} the event`;
}

interface Shown {
  request: TaskDateShiftRequest;
  preview: TaskDateShiftPreview;
}

/**
 * After an event's date moves, offers to move the tasks that follow it — the
 * ones put on from a template that still sit where the template put them.
 *
 * Mounted once, in the app shell; any screen that changes an event's date
 * reaches it through `useUpdateEvent`. Nothing appears when no task would
 * move: the prompt is for a decision, and there is none to make.
 */
export function TaskDateShiftPrompt() {
  const queryClient = useQueryClient();
  const [shown, setShown] = useState<Shown | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The newest request wins: a second date change while the first preview is
  // on its way must not open the older one's answer.
  const latest = useRef(0);

  useEffect(
    () =>
      onTaskDateShiftRequest((request) => {
        const ticket = ++latest.current;
        // A prompt about the earlier change shows days that are no longer the
        // answer; it goes now, whatever the new preview turns out to hold. A
        // move it started may still be running — it no longer owns the prompt.
        setShown(null);
        setSaving(false);
        setError(null);
        previewTaskDateShift(request.eventId, request.from)
          .then((preview) => {
            if (ticket !== latest.current || preview.tasks.length === 0) return;
            setShown({ request, preview });
            setChosen(new Set(preview.tasks.map((t) => t.id)));
            setError(null);
          })
          .catch(() => {
            // Nothing to offer is the quiet outcome; the date change stands.
          });
      }),
    [],
  );

  if (!shown) return null;
  const { request, preview } = shown;
  const close = () => setShown(null);

  const toggle = (id: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const move = async () => {
    // Only the request this move belongs to may close the prompt or show the
    // move's error — a newer date change may have opened another meanwhile.
    const ticket = latest.current;
    const current = () => ticket === latest.current;
    setSaving(true);
    setError(null);
    try {
      const ids = [...chosen];
      for (let i = 0; i < ids.length; i += TASK_DATE_SHIFT_CHUNK) {
        await applyTaskDateShift(
          request.eventId,
          request.from,
          preview.to,
          ids.slice(i, i + TASK_DATE_SHIFT_CHUNK),
        );
      }
      await invalidateTasks(queryClient);
      if (current()) close();
    } catch (err) {
      // Chunks already sent have moved; the lists should show it.
      void invalidateTasks(queryClient);
      if (current()) {
        setError(extractApiErrorMessage(err, 'The tasks could not be moved. Try again.'));
      }
    } finally {
      if (current()) setSaving(false);
    }
  };

  const count = chosen.size;
  return (
    <EntityModal
      open
      onClose={close}
      title="Move tasks with the event?"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            className="h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
          >
            Keep their dates
          </button>
          <button
            type="button"
            onClick={() => void move()}
            disabled={count === 0 || saving}
            className="h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Moving…' : `Move ${count} task${count === 1 ? '' : 's'}`}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="flex items-start gap-2 text-sm text-gray-600">
          <CalendarClock className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" aria-hidden="true" />
          These tasks are set a number of days from the event. Its date changed, so they can move
          with it.
        </p>
        {error && (
          <p role="alert" className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">
            {error}
          </p>
        )}
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg max-h-80 overflow-auto">
          {preview.tasks.map((task) => (
            <li key={task.id}>
              <label className="flex items-start gap-3 px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
                  checked={chosen.has(task.id)}
                  onChange={() => toggle(task.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-gray-900 truncate">{task.title}</span>
                  <span className="flex flex-wrap items-center gap-1 text-xs text-gray-500">
                    <span>{formatDay(task.fromDueDate)}</span>
                    <ArrowRight className="h-3 w-3" aria-label="to" />
                    <span className="text-gray-700">{formatDay(task.toDueDate)}</span>
                    <span>· {describeOffset(task.offsetDays)}</span>
                    {task.listTitle && <span>· {task.listTitle}</span>}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        {preview.keptByHand > 0 && (
          <p className="text-xs text-gray-500">
            {preview.keptByHand === 1
              ? '1 task whose date was changed by hand keeps its date.'
              : `${preview.keptByHand} tasks whose dates were changed by hand keep their dates.`}
          </p>
        )}
      </div>
    </EntityModal>
  );
}
